import { useEffect, useState } from 'react'
import { Dropdown, Flex, message } from 'antd'
import { Mic, MicOff, Dot } from 'lucide-react'
import { useSnapshot } from 'valtio'
import { SessionStatuTypes } from '@/constants'
import styled from 'styled-components'

import { chatState, chatAction } from '@/store'
import useRecorder from '@/hooks/useRecorder'
import ActionIcon from './ActionIcon'

const Warper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  background-color: #1c1f231a;
  border-radius: 50%;
  padding: 4px;
  .wave {
    width: 20px;
    height: 20px;
    --c: no-repeat
      linear-gradient(to right, #9747ff 0%, #7b61ff 50%, #2e90fa 100%);
    background: var(--c) 0% 50%, var(--c) 25% 50%, var(--c) 50% 50%,
      var(--c) 75% 50%, var(--c) 100% 50%;
    background-size: 10% 20%, 10% 40%, 10% 60%, 10% 40%, 10% 20%;
    animation: audioWave 1.5s infinite ease-in-out;
  }
  @keyframes audioWave {
    0% {
      background-size: 10% 20%, 10% 40%, 10% 60%, 10% 40%, 10% 20%;
    }
    /* 20%,
    80% {
      background-size: 10% 30%, 10% 50%, 10% 50%, 10% 50%, 10% 30%;
    }
    30%,
    70% {
      background-size: 10% 40%, 10% 60%, 10% 40%, 10% 60%, 10% 40%;
    }
    40%,
    60% {
      background-size: 10% 50%, 10% 50%, 10% 30%, 10% 50%, 10% 50%;
    } */
    50% {
      background-size: 10% 60%, 10% 40%, 10% 20%, 10% 40%, 10% 60%;
    }
  }
`
const AudioWave = () => {
  return (
    <Warper>
      <div className="wave" />
    </Warper>
  )
}

const MicSvgIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 1.66675C9.33696 1.66675 8.70107 1.93014 8.23223 2.39898C7.76339 2.86782 7.5 3.50371 7.5 4.16675V10.0001C7.5 10.6631 7.76339 11.299 8.23223 11.7678C8.70107 12.2367 9.33696 12.5001 10 12.5001C10.663 12.5001 11.2989 12.2367 11.7678 11.7678C12.2366 11.299 12.5 10.6631 12.5 10.0001V4.16675C12.5 3.50371 12.2366 2.86782 11.7678 2.39898C11.2989 1.93014 10.663 1.66675 10 1.66675Z"
      stroke="#1C1F23"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.8332 8.33325V9.99992C15.8332 11.547 15.2186 13.0307 14.1246 14.1247C13.0307 15.2187 11.5469 15.8333 9.99984 15.8333C8.45274 15.8333 6.96901 15.2187 5.87505 14.1247C4.78109 13.0307 4.1665 11.547 4.1665 9.99992V8.33325"
      stroke="#1C1F23"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.0002 15.8333L10 18.9987"
      stroke="#1C1F23"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 18.9988H8"
      stroke="#1C1F23"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const MicAction = () => {
  const { sessionStatu } = useSnapshot(chatState)
  const [recordText, setRecordText] = useState('')

  const { recStatus, recOpen, recStop, duration, formateDuration } =
    useRecorder(import.meta.env.VITE_APP_ASR_WS_URL, {
      onOpen: () => {
        chatAction.setInputText('')
        chatState.asrSessionId = ''
        setRecordText('')
      },
      onMessage: (data: any) => {
        if (data.type === 'server_ready' && data.session_id) {
          chatState.asrSessionId = data.session_id
        }
        parseAsrResult(data)
      },
      onClose: (event: any) => {
        if (chatState.inputText?.endsWith(',')) {
          const text = chatState.inputText.slice(0, -1)
          chatAction.setInputText(text)
        }
      },
    })

  const parseAsrResult = (data: any) => {
    let sentence = ''
    if (data.type === 'partial_result') {
      let nbest = JSON.parse(data.nbest)
      sentence = nbest[0]?.sentence
      if (nbest.length > 0) {
        chatAction.setInputText(recordText ? recordText + sentence : sentence)
      }
    }
    if (data.type === 'final_result') {
      let nbest = JSON.parse(data.nbest)
      sentence = nbest[0]?.sentence
      if (nbest.length > 0) {
        chatAction.setInputText(recordText ? recordText + sentence : sentence)
        setRecordText(recordText ? recordText + sentence : sentence)
      }
    }
  }

  const onClick = () => {
    // recStatus === 'start' ? recStop() : recOpen()
    if (recStatus === 'start') {
      recStop()
      return
    }
    // 检查会话状态，只有在 ready 状态时才允许开始录音
    if (sessionStatu !== SessionStatuTypes.ready) {
      message.warning('请等待对话完成后再使用语音输入')
      return
    }
    recOpen()
  }

  useEffect(() => {
    if (recStatus === 'start' && sessionStatu !== SessionStatuTypes.ready) {
      recStop()
    }
  }, [recStatus, recStop, sessionStatu])

  return (
    // <Dropdown
    //   menu={{
    //     activeKey: 'time',
    //     items: [
    //       {
    //         key: 'time',
    //         label: (
    //           <Flex align="center">
    //             <Dot strokeWidth={2.5} color="red" />
    //             <span>{duration > 0 ? formateDuration : '语音识别中...'}</span>
    //           </Flex>
    //         ),
    //       },
    //     ],
    //   }}
    //   open={recStatus === 'start'}
    //   placement="bottom"
    //   trigger={['click']}
    // >
    //   <ActionIcon
    //     title={recStatus === 'start' ? '停止语音输入' : '语音输入'}
    //     Icon={recStatus === 'start' ? AudioWave : Mic}
    //     onClick={onClick}
    //   />
    // </Dropdown>
    <ActionIcon
      className="hover:bg-transparent"
      title={recStatus === 'start' ? '停止语音输入' : '语音输入'}
      Icon={recStatus === 'start' ? AudioWave : MicSvgIcon}
      onClick={onClick}
    />
  )
}

export default MicAction
